/**
 * 📅 [Phase 56.5 — 2026-07-09] Dialog "Mettre en congé rapide (1 jour)"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L2185-2291).
 *
 * Rôle : depuis le panneau Sans job, l'utilisatrice met un employé en congé
 * pour la journée en 1 clic. Cette modal liste les types de congé autorisés
 * (avec restants) et crée le congé approuvé immédiatement.
 * Implémenté en overlay `div fixed` custom (pas Radix Dialog) — legacy pattern.
 *
 * Contrat :
 *   - `data` : { date, employee_id, employee_nom } OU null
 *   - `typesLoading` : loading des types disponibles
 *   - `types` : congeTypesDispo (Array de {key, label, icon, desc, ...})
 *   - `chosenType` : congeType (string key)
 *   - `onChooseType(key)` : callback sélection radio
 *   - `submitting` : congeLoading
 *   - `onCancel()` / `onSubmit()`
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
export default function CongeRapideDialog({
  data, typesLoading, types = [], chosenType, onChooseType, submitting, onCancel, onSubmit,
}) {
  if (!data) return null;
  const someAvailable = types && types.some(t => t.available);
  const allUnavailable = types && types.length > 0 && types.every(t => !t.available);
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onCancel?.()}
      data-testid="dialog-conge-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="dialog-conge-rapide"
      >
        <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <span>📅</span>
          Mettre en congé pour 1 jour
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          <b>{data.employee_nom}</b> · {data.date}
        </p>

        {typesLoading && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="loading-types-conges">
            <span className="inline-block animate-spin">⏳</span> Chargement des types autorisés…
          </div>
        )}

        {!typesLoading && allUnavailable && (
          <div className="py-6 text-center text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded p-3" data-testid="empty-types-conges">
            <p className="text-amber-800 font-medium">Aucun type de congé n'est autorisé pour cet employé.</p>
            <p className="text-xs text-amber-600 mt-1">Configurez les banques dans sa fiche employé d'abord.</p>
          </div>
        )}

        {!typesLoading && someAvailable && (
          <div className="space-y-2" data-testid="liste-types-conge">
            {types.filter(t => t.available).map((opt) => {
              const baseCls = `flex items-start gap-2 p-2.5 rounded border transition cursor-pointer ${
                chosenType === opt.key
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-slate-200 hover:border-purple-300'
              }`;
              return (
                <label
                  key={opt.key}
                  className={baseCls}
                  data-testid={`option-conge-${opt.key}`}
                >
                  <input
                    type="radio"
                    name="conge-type"
                    value={opt.key}
                    checked={chosenType === opt.key}
                    onChange={(e) => onChooseType?.(e.target.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">
                      {opt.icon} {opt.label}
                      {opt.aucune_limite && (
                        <span className="ml-2 text-xs font-normal text-emerald-600">
                          (illimité)
                        </span>
                      )}
                      {!opt.aucune_limite && opt.limite > 0 && (
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          ({(opt.restant ?? 0).toFixed(1)}/{(opt.limite ?? 0).toFixed(0)} restants)
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {opt.desc}
                      {opt.warning && <span className="text-orange-600 font-medium"> — {opt.warning}</span>}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
          ℹ️ Le congé sera créé en statut <b>« Approuvé »</b> immédiatement et l'employé disparaîtra du panneau « Sans job ».
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onCancel?.()}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            data-testid="btn-conge-annuler"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => onSubmit?.()}
            disabled={submitting || !chosenType}
            className="px-4 py-2 text-sm rounded bg-purple-600 hover:bg-purple-700 text-white font-medium disabled:opacity-50"
            data-testid="btn-conge-confirmer"
          >
            {submitting ? 'Création…' : 'Mettre en congé'}
          </button>
        </div>
      </div>
    </div>
  );
}
